// Used in main app
const mode = import.meta.env.MODE
const apiBaseURL = mode === 'production' ? '/api' : ''
export interface InterestRateObj {
  interestPeriod: number
  interestRate: string
  interestRateCreated: string
  referenceId: string
  termsEndDate: string
  termsEndDateCreated: string
}

interface InterestList {
  listInterests: InterestRateObj[]
}

export async function fetchThreeMonthMortgageRates(): Promise<
  | {
      successful: true
      threeMonthInterestRate: number
    }
  | {
      successful: false
    }
> {
  const url = `${apiBaseURL}/interest-mortgage-service/api/v1/interest`

  try {
    const response = await fetch(url)
    const data = (await response.json()) as InterestList
    const { listInterests } = data
    if (!listInterests) throw new Error('Unable to find interest rates info')
    const threeMonthInterestRateObj = listInterests.find(
      (interest) => interest.interestPeriod === 3,
    )
    if (!threeMonthInterestRateObj) throw new Error('Unable to find 3 month interest rate')
    const threeMonthInterestRate = +threeMonthInterestRateObj.interestRate
    return {
      successful: true,
      threeMonthInterestRate,
    }
  } catch (err) {
    return {
      successful: false,
    }
  }
}

export interface MortgageTerms {
  maxTotalDebtForResidence: number
  minLivingSpaceForCondo: number
  maxLivingSpaceForCondo: number
  minLivingSpaceForHouse: number
  maxLivingSpaceForHouse: number
  minMaritalStatus: number
  maxMaritalStatus: number
  maxLoanToValueRatioMortgage: number
  maxLoanToValueRatioMortgageAndPersonalLoan: number
  maxLoanAmountForSecondaryHouse: number
  minLoanAmountMortgage: number
  maxLoanAmountMortgage: number
  maxLoanAmountMortgageAndPersonalLoan: number
  minDeposit: number
  maxDeposit: number
  minHousePrice: number
  minLoanAmountForIncreasedMortgage: number
  minDownPayment: number
  minAgeForLoan: number
  retirementAgeLimit: number
}

export async function fetchMortgageTerms(): Promise<
  | {
      successful: true
      data: MortgageTerms
    }
  | {
      successful: false
    }
> {
  try {
    const response = await fetch(
      'https://api.sbab.se/api/product-terms/3.0/product/loan-application',
    )
    const data = (await response.json()) as MortgageTerms
    return {
      successful: true,
      data,
    }
  } catch (err) {
    return {
      successful: false,
    }
  }
}

interface PriceData {
  priceDifference: string
  priceComponents: [
    {
      type: 'fixed'
      priceDifference: string
    },
  ]
  productOfferId: string
  priceDifferenceSpecification: {
    description: string
    priceDifferenceItems: [
      {
        priceDifference: string
        description: string
        calculationName: 'PriceMatrixCalculation'
        type: 'fixed'
      },
      {
        priceDifference: string
        description: string
        calculationName: 'KilowattLoanCalculation'
        type: 'fixed'
      },
    ]
    limited: boolean
  }
}

interface EffectiveInterest {
  effectiveRate: string
}

// Price diff + effective interest
export async function fetchDataForResultSection(
  marketValue: number,
  mortgageAmount: number,
  threeMonthInterestRate: number,
): Promise<
  | {
      successful: true
      priceMatrixDifference: number
      kilowattLoanDifference: number
      effectiveInterest: number
    }
  | {
      successful: false
    }
> {
  try {
    const response = await fetch(
      `${apiBaseURL}/interest-service/api/v1/interest/difference/market/mortgage/kilowatt`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          marketValue,
          mortgageAmount,
          brokerCodes: ['690001'],
        }),
      },
    )
    const data = (await response.json()) as PriceData
    const priceMatrixDifference = data.priceDifferenceSpecification.priceDifferenceItems.find(
      (item) => item.calculationName === 'PriceMatrixCalculation',
    )?.priceDifference
    if (!priceMatrixDifference) throw new Error('Unable to find price matrix difference')
    const kilowattLoanDifference = data.priceDifferenceSpecification.priceDifferenceItems.find(
      (item) => item.calculationName === 'KilowattLoanCalculation',
    )?.priceDifference
    if (!kilowattLoanDifference) throw new Error('Unable to find price matrix difference')
    const effectiveRateResponse = await fetch(
      `${apiBaseURL}/interest-calculation-service/api/v1/effective-interest/mortgage`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rate: Math.max(0, threeMonthInterestRate + +priceMatrixDifference),
          mortgageAmount: mortgageAmount,
        }),
      },
    )
    const effectiveInterestData = (await effectiveRateResponse.json()) as EffectiveInterest
    const effectiveInterest = +effectiveInterestData.effectiveRate
    return {
      successful: true,
      priceMatrixDifference: +priceMatrixDifference,
      kilowattLoanDifference: +kilowattLoanDifference,
      effectiveInterest,
    }
  } catch (err) {
    return {
      successful: false,
    }
  }
}

// Used in EffectiveRateAccordionSection
export interface TotalCostData {
  totalPayment: number
  totalInterestPayment: number
}

export async function fetchTotalCost(rate: number, loanAmount: number) {
  try {
    const url = `${apiBaseURL}/interest-calculation-service/api/v2/total-cost/simplified`
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        rate,
        amount: loanAmount,
        years: 40,
      }),
    }
    const response = await fetch(url, options)
    const data = (await response.json()) as TotalCostData
    return {
      successful: true,
      data,
    }
  } catch (err) {
    return {
      successful: false,
      data: null,
    }
  }
}
